import React, { Fragment, useEffect, useState, useRef } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";
import CustomInput from "../../../../common/CustomInput";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import MedicationCreate from "../clinical-assessment/MedicationCreate";
import Button from "../../../../common/Button";
import { drugTypes } from "../clinical-assessment/data";

const AdditionalMedicationCard = ({
  medicationCheckListData,
  onFocus,
  enableProfileAnalysis,
  patientId,
  initialComments = null,
  isCompleted = false,
  onEditModeChange = () => {},
  completeAnalysis = () => {},
  forceDisabled = false,
}) => {
  const [questionArray, setQuestionArray] = useState([]);
  const [addedQuestions, setAddedQuestions] = useState([]);
  const [comments, setComments] = useState(null);
  const bodyRef = useRef(null);
  const questionRefs = useRef([]);
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const hasPersistedData =
    (medicationCheckListData ?? []).some((q) => q?.isYes != null) ||
    (initialComments?.trim?.().length ?? 0) > 0;

  const isCompletedLocked = isCompleted && !isEditMode && hasPersistedData;
  const isForceLocked = forceDisabled && !isEditMode;
  const isLocked = isCompletedLocked || isForceLocked;
  const allAnswered = !questionArray.some((q) => q?.isYes == null);

  useEffect(() => {
    setQuestionArray(medicationCheckListData.map((item) => ({ ...item })));
  }, [medicationCheckListData]);

  useEffect(() => {
    setComments(initialComments ?? "");
  }, [initialComments]);

  const handleSelection = (id, field, val) => {
    // Check if the object was found
    if (id !== -1) {
      // Find the index of the object with the given id
      const med = questionArray.find((card) => card.id === id);
      const index = questionArray.findIndex((card) => card.id === med.id);

      // Update the specified field with the new value
      const updatedMed = { ...med, [field]: val };

      // Create a new array with the updated object
      const updatedMedCards = [
        ...questionArray.slice(0, index),
        updatedMed,
        ...questionArray.slice(index + 1),
      ];
      setQuestionArray(updatedMedCards);
      setActiveIndex(index + 1);
    }
  };

  useEffect(() => {
    if (activeIndex == null) return;

    const nextEl = questionRefs.current[activeIndex] || questionRefs.current[activeIndex - 1];

    if (!nextEl) return;

    nextEl.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [activeIndex]);

  return (
    <div
      className={`flex flex-col flex-1 gap-4 bg-white ${
        onFocus && "border-2 border-REELTeal"
      }`}
    >
      <>
        <div className="flex flex-row">
          <div
            className={`flex flex-row ${"w-full overflow-x-auto overflow-y-hidden scroll-smooth"}`}
            id="review"
          >
            <>
              <div className="rounded-[4px] border border-gray-100 h-[589px] max-w-full flex flex-col">
                {/* header */}
                <div className="bg-gray-20 h-[77px] px-8 pt-6 pb-3 gap-5 flex-shrink-0">
                  <div className="flex flex-row justify-between pr-5">
                    <h3 className="text-2xl font-semibold text-teal-300 h-[52px]">Additional Medications</h3>
                    {isCompleted && hasPersistedData && (
                      <Menu as="div" className="relative">
                        <div>
                          <Menu.Button>
                            <ReactSVG src="/icons/triple-dot.svg" className={`flex w-2 scale-x-[1.6] scale-y-[1.6]`} />
                          </Menu.Button>
                        </div>
                        <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform translate-y-2 opacity-0 scale-95" enterTo="transform translate-y-0 opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform translate-y-0 opacity-100 scale-100" leaveTo="transform translate-y-2 opacity-0 scale-95">
                          <Menu.Items className="absolute right-0 top-0 mt-8 w-24 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-[1000]">
                            <Menu.Item>
                              <button className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100" onClick={() => { setIsEditMode(true); onEditModeChange(true); }}>
                                Edit
                              </button>
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}
                  </div>
                </div>

                {/* scrollable body */}
                <div ref={bodyRef} className="flex-1 overflow-auto px-8 pt-5 pb-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-6">
                      {questionArray.map((q, i) => (
                        <div ref={(el) => (questionRefs.current[i] = el)} key={q.id ?? i} className={`flex flex-row gap-4 min-h-[64px] pb-3 border-b-[1px] border-gray-100 items-start ${isLocked ? "pointer-events-none" : ""}`}>
                          <div className="flex-1 min-w-0 text-teal-300">
                            <span className="text-lg font-medium block break-words whitespace-normal">
                              {q.question}
                            </span>
                            {q.details && (
                              <div className="text-sm text-gray-600 mt-1 break-words whitespace-normal">
                                {q.details}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-row justify-between max-w-[262px]">
                            <button className={`font-semibold text-body-md max-h-[52px] p-3 border-[1px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100 rounded-[4px] ${q.isYes ? "bg-blue-100 min-w-[163px] border-r-0 rounded-r-none" : "bg-white min-w-[262px]"}`} disabled={isLocked} onClick={() => { handleSelection(q.id, "isYes", true); }}>
                              <p className="font-semibold text-body-md flex justify-center items-center">Yes</p>
                            </button>
                            {q.isYes && (
                              <Button type={"primary-outline"} className="!w-[129px] !max-h-[52px] !rounded-l-none" text={"+ Add"} disabled={isLocked} onClick={() => setShowMedicationSlider(true)} />
                            )}
                          </div>
                          <button className={`font-semibold max-h-[52px] text-body-md p-3 rounded-[4px] border-[1px] min-w-[262px] border-REELTeal text-teal-300 hover:text-teal-300 hover:bg-blue-100 ${q.isYes === false ? "bg-blue-100" : "bg-white"}`} disabled={isLocked} onClick={() => { handleSelection(q.id, "isYes", false); if (addedQuestions.includes(q.id)) setAddedQuestions(addedQuestions.filter((i) => i !== q.id)); }}>
                            <p className="font-semibold text-body-md flex justify-center items-center">No</p>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* footer - comments + action */}
                <div className="px-8 py-4 bg-gray-20 border-t border-gray-100 flex-shrink-0">
                  <div className="flex flex-row space-x-5">
                    <CustomInput name="comments" className="col-span-12 w-full !min-h-[52px] bg-white" placeholder="Comments" inputClassName="!placeholder-gray-600" showOptionalLabel={false} value={comments} onChange={(e) => { setComments(e.target.value); }} type="text" disabled={isLocked} />
                    {isCompletedLocked ? (
                      <div className="flex flex-row justify-center min-w-[262px] min-h-[62px] pl-16 pr-2">
                        <span className="w-fit text-REELTeal font-bold text-base items-center flex">
                          Complete
                        </span>
                        <ReactSVG
                          src="/icons/medication-card-complete.svg"
                          className="pl-4 w-fit flex items-center justify-center"
                        />
                      </div>
                    ) : (
                      <button className={`font-semibold text-body-md p-3 rounded-[4px] border-[1px] min-w-[262px] bg-REELTeal border-transparent hover:bg-teal-200 text-white disabled:opacity-50`} disabled={!allAnswered} onClick={() => { completeAnalysis({ arr: questionArray, comments: comments }); setIsEditMode(false); onEditModeChange(false); }}>
                        <p className="font-semibold text-body-md flex justify-center items-center">{isEditMode ? "Update & Continue" : questionArray.length > 0 && medicationCheckListData.every((item, index) => item.isYes === questionArray[index].isYes) ? "Continue" : "Save & Continue"}</p>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          </div>
        </div>
      </>
      <MedicationCreate
        create={true}
        patientId={patientId}
        type="Medication"
        open={showMedicationSlider}
        selectedDrugType={drugTypes.Additional}
        step={1}
        maxSteps={1}
        onClose={(value) => {
          setShowMedicationSlider(value);
        }}
        onSave={() => {
          setShowMedicationSlider(false);
        }}
      />
    </div>
  );
};

export default AdditionalMedicationCard;
